/**
 * Latency-first correction: correct a latency problem with queue-drain levers that preserve
 * frame rate, and only use an IDR when the queue has fully drained and the stream is clean.
 *
 * The field data showed the climb's IDR hitching the stream mid-episode. The correction
 * ordering is: bitrate cut → resolution downgrade → fps step-down → IDR. The IDR gate is
 * closed while any latency signal is active, and opens only on the final clean climb.
 */

import type { DecodeLagEpisode } from './decodeLagContracts';
import type { GuestBrowserCapability } from './guestBrowserCapability';
import type { QualityTier } from './webrtcLatencyContracts';

/** The correction lever ordering: bitrate first, then resolution, then fps, then IDR. */
export const LATENCY_CORRECTION_ORDER = ['bitrate', 'resolution', 'fps', 'idr'] as const;
export type LatencyCorrectionLever = (typeof LATENCY_CORRECTION_ORDER)[number];

/**
 * The lever ordering where a resolution change costs a decoder reconfiguration. On Android
 * the 360p switch tore down and rebuilt the hardware decoder and roughly doubled the standing
 * buffer it was meant to relieve (2026-09-19 field sessions), so `resolution` is not a
 * latency lever there at all: bitrate, then fps, then the IDR.
 */
export const DECODER_RECONFIGURE_LATENCY_CORRECTION_ORDER = ['bitrate', 'fps', 'idr'] as const;

/**
 * The correction lever ordering for a guest engine. Keyed on the capability's
 * `resolutionChangeReconfiguresDecoder` flag (Android MediaCodec and WebKit VideoToolbox both
 * rebuild the decoder on a resolution change; desktop Chrome does not). A caller that only
 * knows the engine may pass `{ engine }` alone: `android-chrome` is then taken as
 * reconfiguring, matching the matrix. Undefined (engine unknown) keeps the full ordering.
 */
export function latencyCorrectionOrderFor(
  capability: Partial<Pick<GuestBrowserCapability, 'engine' | 'resolutionChangeReconfiguresDecoder'>> | undefined,
): readonly LatencyCorrectionLever[] {
  if (!capability) return LATENCY_CORRECTION_ORDER;
  const reconfigures =
    capability.resolutionChangeReconfiguresDecoder ?? capability.engine === 'android-chrome';
  return reconfigures ? DECODER_RECONFIGURE_LATENCY_CORRECTION_ORDER : LATENCY_CORRECTION_ORDER;
}

/**
 * The next lever to apply for a latency problem, given the current episode.
 *
 * Returns null when the queue is drained and the stream is clean. The ordering is:
 * bitrate (the cheapest, no hitch), then resolution (the 360p downgrade, no hitch),
 * then fps (the ladder, no hitch), then IDR (the hitch, only as the final clear).
 */
export function nextLatencyCorrectionLever(
  episode: DecodeLagEpisode | null,
  currentTier: QualityTier,
  currentFps: number,
): LatencyCorrectionLever | null {
  if (!episode || !episode.active) return null;
  // Bitrate is always the first lever (it never hitches and it is the cheapest cut).
  if (currentTier !== 'bottom') return 'bitrate';
  // On the floor (360p), step fps down the ladder before the IDR.
  if (currentFps > 30) return 'fps';
  // On the floor at the lowest fps: the IDR is the last resort.
  return 'idr';
}

/**
 * The IDR gate: false while any latency signal is active; true only on the final clean climb.
 */
export function shouldRequestIdr(
  episode: DecodeLagEpisode | null,
  queueDrained: boolean,
  climbComplete: boolean,
): boolean {
  // An active episode never gets an IDR.
  if (episode?.active) return false;
  // The IDR fires only on the final clean climb.
  return queueDrained && climbComplete;
}
